import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  PackingListModel,
  PackingListStatus,
} from '@core/app/scripts/classes/packing-list-model';
import { Sort } from '@classes/sort';
import { Domain } from '@shared/app/scripts/services/domain';

export type PackingListFilter = {
  /**
   * discouraged, if known better use getById()
   */
  _id?: string;

  active?: boolean;

  /**
   * date represented as string
   */
  startDate?: string;

  /**
   * date represented as string
   */
  endDate?: string;

  /**
   * date represented as string
   */
  startModifiedDate?: string;

  /**
   * date represented as string
   */
  endModifiedDate?: string;

  /**
   * most likely alias for _id
   */
  docNumber?: string;

  /**
   * probably customer._id
   */
  customerId?: string;

  /**
   * status we are interested in
   */
  status?: PackingListStatus | PackingListStatus[];

  /**
   * Regex search term
   */
  text?: string;

  /**
   * empty object
   */
  type?: {};

  /**
   * date represented as string
   */
  reservationDateMin?: string;

  /**
   * date represented as string
   */
  reservationDateMax?: string;

  /**
   * probably location._id
   */
  warehouseId?: string;

  /**
   * ?
   */
  customerName?: string;

  /**
   * ?
   */
  barcode?: string;

  /**
   * ?
   */
  articleCode?: string;
};

// TODO consider moving out to new module shared between picking & logistics
/**
 * Service to expose /packing-lists/* endpoints
 */
@Injectable()
export class PackingListService {
  constructor(
    @Inject(HttpClient) private httpClient: HttpClient,
    @Inject(Domain) private domain: Domain,
  ) {}

  /**
   * Downloads requested PackingList as pdf.
   * @param _id PackingList id
   * @returns Pdf file as string
   */
  download(_id: string): Observable<string> {
    return this.httpClient.post<string>(
      // probably should go through services
      `${this.domain.getSpynlService()}/packing-lists/download`,
      { _id },
      // TODO map here this ng1 values
      // {
      //   responseType: 'arraybuffer',
      //   withCredentials: true
      // }
    );
  }

  /**
   * Downloads requested PackingList as excel file.
   * @param _id PackingList id
   * @returns Excel file as string
   */
  downloadExcel(_id: string): Observable<string> {
    return this.httpClient.post<string>(
      // probably should go through services
      `${this.domain.getSpynl()}/packing-lists/download-excel`,
      { _id },
    );
  }

  /**
   * Downloads requested PackingList as CSV.
   * @param _id PackingList id
   * @returns CSV file as string
   */
  downloadCsv(_id: string): Observable<string> {
    return this.httpClient.post<string>(
      // probably should go through services
      `${this.domain.getSpynl()}/packing-lists/download-csv`,
      { _id },
    );
  }

  /**
   * Overrides existing packingList
   * @param packingList to be saved
   * @returns result of the operation
   */
  save(packingList: PackingListModel): Observable<any> {
    return this.httpClient.post<{ status: 'ok' | 'error'; data: [string] }>(
      `${this.domain.getSpynl()}/packing-lists/save`,
      { data: packingList },
    );
  }

  /**
   * Get one packingList by it's id
   * @param _id packing list to be loaded
   * @returns the packingList that was found
   */
  getById(_id: string): Observable<PackingListModel> {
    return this.httpClient
      .post<{ data: [PackingListModel] }>(
        `${this.domain.getSpynl()}/packing-lists/get`,
        {
          filter: {
            _id,
          },
        },
      )
      .pipe(map((response) => new PackingListModel(response.data[0])));
  }

  /**
   * Get all packingLists
   * @returns all packing lists
   */
  getAll(): Observable<{ data: PackingListModel[] }> {
    return this.httpClient.post<{ data: PackingListModel[] }>(
      `${this.domain.getSpynl()}/packing-lists/get`,
      {},
    );
  }

  /**
   * Get all packingLists that fits into filter
   * @param filter object
   * @param limit bunch size
   * @param skip number of elements to be skipped
   * @param fields to be displayed
   * @param sort as array or arrays
   * @returns match packing lists
   */
  filter(
    filter: PackingListFilter,
    limit: number = 100,
    skip: number = 0,
    fields?: string[],
    sort?: Sort,
  ): Observable<{ data: PackingListModel[] }> {
    return this.httpClient.post<{ data: PackingListModel[] }>(
      `${this.domain.getSpynl()}/packing-lists/get`,
      {
        filter,
        skip,
        limit,
        fields,
        sort,
      },
    );
  }
}
